import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import type { Dispatch } from 'redux';
import { useCallback } from 'react';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  getPlanData,
  getPlanDataErknmStatus,
  getPlanWatchOnlyByTestUser,
} from 'PlanPackage/MainModule/selectors/planSelectors';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import {
  getIsPlanErknmStatusApproved,
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusInProcess,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ListTypeEnum } from 'APIPackage/CatalogsModule/enums/ListTypeEnum';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { ROUTE_KNM_LIST_BY_ID_AND_TYPE } from 'CorePackage/MainModule/routes/routes';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { getAccountOrganizationsData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { planExportPlanById } from 'PlanPackage/MainModule/actions/planActions';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { KnmErknmStatusIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusIdsByCodeEnum';

import { getKnmListByPlanData } from '../selectors/knmListByPlanSelectors';
import { knmListByPlanApproveAction } from '../actions/knmListByPlanActions';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';

interface IKnmListByPlanDropdownProps {
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  plan: IKnmPlan | undefined;
  itemsCheck: TableCheckItems;
  knms: IKnmRaw[];
  isDropdownDeleteItemsDisabled: boolean;
  selectedKnmsIds: string[];
  selectedKnmsNumber: number;
  handleAddKnmButtonClick: () => void;
  handleUploadButtonClick: () => void;
  handleDropdownDeleteButtonClick: () => void;
  handleDropdownExcludeButtonClick: () => void;
  isWatchOnlyByTestUser: boolean;
  handleApproveRemarksModalOpen: () => void;
  isErknm: boolean;
}

const KnmListByPlanDropdownWithoutStore = ({
  isSupervisor,
  isProsecutorOrSuperProsecutor,
  planErknmStatus,
  plan,
  itemsCheck,
  knms,
  isDropdownDeleteItemsDisabled,
  selectedKnmsIds,
  selectedKnmsNumber,
  handleAddKnmButtonClick,
  handleUploadButtonClick,
  handleDropdownDeleteButtonClick,
  handleDropdownExcludeButtonClick,
  isWatchOnlyByTestUser,
  handleApproveRemarksModalOpen,
  isErknm,
}: IKnmListByPlanDropdownProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();

  const isPlanErknmStatusInProcess: boolean = getIsPlanErknmStatusInProcess(planErknmStatus);
  const isPlanErknmStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(planErknmStatus);
  const isPlanErknmStatusConsidered: boolean = getIsPlanErknmStatusConsidered(planErknmStatus);
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);
  const knmIds = Object.keys(itemsCheck)
    .filter(key => itemsCheck[key])
    .map(item => +item);
  const filteredKnms = knms.filter(el => selectedKnmsIds.find(knmId => Number(knmId) === el.id));

  const isSomeSelectedKnmDeleted = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.deleted),
  );

  const isDropdownVisibleSelected = selectedKnmsNumber === 0 || (selectedKnmsNumber > 0 && isSomeSelectedKnmDeleted);
  const isDropdownExcludeKnmEnable =
    !isDropdownVisibleSelected &&
    filteredKnms.length &&
    (filteredKnms.every(knm => knm.statusId === KnmErknmStatusIdsByCodeEnum.TYPE_WAITING_CARRY_OUT) ||
      (isProsecutorOrSuperProsecutor && isPlanErknmStatusUnderConsideration)) &&
    filteredKnms.every(knm => knm.statusId !== KnmErknmStatusIdsByCodeEnum.REJECTED);

  const exportPlanById: (format: ExportFormatsEnum, id: number, approved?: boolean, considered?: boolean) => void = (
    format: ExportFormatsEnum,
    id: number,
    approved?: boolean,
    considered?: boolean,
  ) => dispatch(planExportPlanById(format, id, approved, considered));

  const handleExportPlan = (
    format: ExportFormatsEnum,
    id?: number | string,
    approved?: boolean,
    considered?: boolean,
  ) => () => {
    if (id !== undefined) {
      exportPlanById(format, Number(id), approved, considered);
    }
  };

  const handleDropdownApproveButtonClick = useCallback(() => {
    dispatch(knmListByPlanApproveAction(knmIds));
  }, [dispatch, knmIds]);

  const toAddKnm: IDropdownItem[] = [
    {
      id: 'to-add-knm',
      content: <DropdownItem onClick={handleAddKnmButtonClick}>Добавить КНМ</DropdownItem>,
    },
  ];

  const navigateToTotalKnms = useCallback(() => {
    history.push(ROUTE_KNM_LIST_BY_ID_AND_TYPE(plan?.id || '', ListTypeEnum.TOTAL));
  }, [history, plan?.id]);

  const toTotalKnms: IDropdownItem[] = [
    {
      id: 'to-total-knms',
      content: <DropdownItem onClick={navigateToTotalKnms}>Просмотр полного состава плана</DropdownItem>,
    },
  ];

  const navigateToAgreedKnms = useCallback(() => {
    history.push(ROUTE_KNM_LIST_BY_ID_AND_TYPE(plan?.id || '', ListTypeEnum.AGREED));
  }, [history, plan?.id]);

  const toAgreedKnms: IDropdownItem[] = [
    {
      id: 'to-agreed-knms',
      content: <DropdownItem onClick={navigateToAgreedKnms}>Просмотр согласованного плана</DropdownItem>,
    },
  ];

  const toAddAdjustments: IDropdownItem[] = [
    {
      id: 'to-add-adjustments',
      content: <DropdownItem onClick={handleUploadButtonClick}>Загрузить корректировки</DropdownItem>,
    },
  ];

  const toExportConsidered: IDropdownItem[] = [
    {
      id: 'to-add-considered-ods',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id, false, true)}>
          Экспорт рассмотренного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-add-considered-xlsx',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id, false, true)}>
          Экспорт рассмотренного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toExportApproved: IDropdownItem[] = [
    {
      id: 'to-add-approved-ods',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id, true)}>
          Экспорт утвержденного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-add-approved-xlsx',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id, true)}>
          Экспорт утвержденного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toFullExport: IDropdownItem[] = [
    {
      id: 'to-full-export-ods',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id)}>
          Экспорт полного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-full-export-xlsx',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id)}>
          Экспорт полного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toDeleteKnm: IDropdownItem[] = [
    {
      id: 'to-delete-knm',
      content: (
        <DropdownItem onClick={handleDropdownDeleteButtonClick} isDanger isDisabled={isDropdownDeleteItemsDisabled}>
          Удалить выбранное
        </DropdownItem>
      ),
    },
  ];

  const toApproveRemarksKnm: IDropdownItem[] = [
    {
      id: 'to-approve-remarks-knm',
      content: (
        <DropdownItem onClick={handleApproveRemarksModalOpen} isDisabled={isDropdownVisibleSelected}>
          Согласовано с замечанием
        </DropdownItem>
      ),
    },
  ];
  const toApproveKnm: IDropdownItem[] = [
    {
      id: 'to-approve-knm',
      content: (
        <DropdownItem onClick={handleDropdownApproveButtonClick} isDisabled={isDropdownVisibleSelected}>
          Согласовать
        </DropdownItem>
      ),
    },
  ];
  const toExcludeKnm: IDropdownItem[] = [
    {
      id: 'to-exclude-knm',
      content: (
        <DropdownItem onClick={handleDropdownExcludeButtonClick} isDanger isDisabled={!isDropdownExcludeKnmEnable}>
          Исключить из плана
        </DropdownItem>
      ),
    },
  ];

  const dropdownItems = () => {
    let res: IDropdownItem[] = [];

    if (!isWatchOnlyByTestUser && isSupervisor) {
      if (isPlanErknmStatusInProcess) {
        res = [...res, ...toAddKnm];
      }
      if (isPlanErknmStatusApproved) {
        res = [...res, ...toAddKnm];
      }
    }
    if (!isPlanErknmStatusUnderConsideration) {
      res = [...res, ...toTotalKnms];
    } else {
      res = [...res, ...toAgreedKnms];
    }
    if (isProsecutorOrSuperProsecutor && isPlanErknmStatusUnderConsideration && !isErknm) {
      res = [...res, ...toAddAdjustments];
    }
    if (isPlanErknmStatusConsidered) {
      res = [...res, ...toExportConsidered];
    }
    if (isPlanErknmStatusApproved) {
      res = [...res, ...toExportApproved];
    }
    if (!isWatchOnlyByTestUser && isPlanErknmStatusInProcess) {
      res = [...res, ...toDeleteKnm];
    }
    if (isSupervisor) {
      if (isPlanErknmStatusApproved || isPlanErknmStatusUnderConsideration) {
        res = [...res, ...toExcludeKnm];
      }
    }
    res = [...res, ...toFullExport];
    if (isProsecutorOrSuperProsecutor && isPlanErknmStatusUnderConsideration) {
      res = [...res, ...toApproveRemarksKnm, ...toApproveKnm, ...toExcludeKnm];
    }

    return res;
  };

  return <Dropdown items={dropdownItems()} />;
};

const mapStateToProps = (state: IRootState) => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  userProsecutorOrganisations: getAccountOrganizationsData(state).prosecutor,
  plan: getPlanData(state),
  knms: getKnmListByPlanData(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const KnmListByPlanDropdown = connect(mapStateToProps)(KnmListByPlanDropdownWithoutStore);
